import CRMUtils from ".";

const CommentsUtils = {
  /**
   * Checks if the user has permission to edit a comment.
   *
   * @param {Object} user - The user object, typically containing the user ID and permissions.
   * @param {Object} comment - The comment object, including the `user_id` and `commentable_type` fields.
   * @param {string} comment.user_id - The ID of the user who created the comment.
   * @param {string} comment.commentable_type - The type of the entity the comment is associated with (e.g., Contact, Quote, Invoice).
   * @returns {boolean} - Returns `true` if the user can edit the comment, otherwise `false`.
   */
  userCanEditComment(user, comment) {
    // If the comment has no user ID, editing is not allowed
    if (!comment.user_id || !comment.editable) {
      return false;
    }

    // Initialize variables for user permissions
    var userCanEditComment = false; // To track if the user can edit comments
    var userCanEditCommentSuper = false; // To track if the user has "super" permissions to edit comments

    // Check permissions based on the type of entity the comment is associated with (commentable_type)
    switch (comment.commentable_type) {
      case "App\\Models\\Contact":
        // Check if the user has regular or super permission to edit Contact comments
        userCanEditComment = CRMUtils.userHasPermission(user, [
          "contacts_editComments",
        ]);
        userCanEditCommentSuper = CRMUtils.userHasPermission(user, [
          "contacts_editCommentsSuper",
        ]);
        break;
      case "App\\Models\\Quote":
        // Check if the user has regular or super permission to edit Quote comments
        userCanEditComment = CRMUtils.userHasPermission(user, [
          "quote_editComments",
        ]);
        userCanEditCommentSuper = CRMUtils.userHasPermission(user, [
          "quote_editCommentsSuper",
        ]);
        break;
      case "App\\Models\\Invoice":
        // Check if the user has regular or super permission to edit Invoice comments
        userCanEditComment = CRMUtils.userHasPermission(user, [
          "invoice_editComments",
        ]);
        userCanEditCommentSuper = CRMUtils.userHasPermission(user, [
          "invoice_editCommentsSuper",
        ]);
        break;
      case "App\\Models\\Record":
        // Check if the user has regular or super permission to edit Record comments
        userCanEditComment = CRMUtils.userHasPermission(user, [
          "record_editComments",
        ]);
        userCanEditCommentSuper = CRMUtils.userHasPermission(user, [
          "record_editCommentsSuper",
        ]);
        break;
      case "App\\Models\\RecordProduct":
        // Check if the user has regular or super permission to edit RecordProduct comments
        userCanEditComment = CRMUtils.userHasPermission(user, [
          "recordproduct_editComments",
        ]);
        userCanEditCommentSuper = CRMUtils.userHasPermission(user, [
          "recordproduct_editCommentsSuper",
        ]);
        break;
      case "App\\Models\\Ticket":
        // Check if the user has regular or super permission to edit Ticket comments
        userCanEditComment = CRMUtils.userHasPermission(user, [
          "tickets_editComments",
        ]);
        userCanEditCommentSuper = CRMUtils.userHasPermission(user, [
          "tickets_editCommentsSuper",
        ]);
        break;
      case "App\\Models\\Influencer":
        // Check if the user has regular or super permission to edit Influencer comments
        userCanEditComment = CRMUtils.userHasPermission(user, [
          "marketing_editInfluencerComments",
        ]);
        userCanEditCommentSuper = CRMUtils.userHasPermission(user, [
          "marketing_editInfluencerCommentsSuper",
        ]);
        break;
    }

    // Return true if the user has super permission or the user can edit and is the author of the comment
    if (
      userCanEditCommentSuper ||
      (userCanEditComment && comment.user_id == user.id)
    ) {
      return true;
    } else {
      return false;
    }
  },

  /**
   * Checks if the user has permission to delete a comment.
   *
   * @param {Object} user - The user object, typically containing the user ID and permissions.
   * @param {Object} comment - The comment object, including the `user_id` and `commentable_type` fields.
   * @param {string} comment.user_id - The ID of the user who created the comment.
   * @param {string} comment.commentable_type - The type of the entity the comment is associated with (e.g., Contact, Quote, Invoice).
   * @returns {boolean} - Returns `true` if the user can delete the comment, otherwise `false`.
   */
  userCanDeleteComment(user, comment) {
    // If the comment has no user ID, deleting is not allowed
    if (!comment.user_id || !comment.editable) {
      return false;
    }

    // Initialize variables for user permissions
    var userCanDeleteComment = false; // To track if the user can delete comments
    var userCanDeleteCommentSuper = false; // To track if the user has "super" permissions to delete comments

    // Check permissions based on the type of entity the comment is associated with (commentable_type)
    switch (comment.commentable_type) {
      case "App\\Models\\Contact":
        // Check if the user has regular or super permission to delete Contact comments
        userCanDeleteComment = CRMUtils.userHasPermission(user, [
          "contacts_deleteComments",
        ]);
        userCanDeleteCommentSuper = CRMUtils.userHasPermission(user, [
          "contacts_deleteCommentsSuper",
        ]);
        break;
      case "App\\Models\\Quote":
        // Check if the user has regular or super permission to delete Quote comments
        userCanDeleteComment = CRMUtils.userHasPermission(user, [
          "quote_deleteComments",
        ]);
        userCanDeleteCommentSuper = CRMUtils.userHasPermission(user, [
          "quote_deleteCommentsSuper",
        ]);
        break;
      case "App\\Models\\Invoice":
        // Check if the user has regular or super permission to delete Invoice comments
        userCanDeleteComment = CRMUtils.userHasPermission(user, [
          "invoice_deleteComments",
        ]);
        userCanDeleteCommentSuper = CRMUtils.userHasPermission(user, [
          "invoice_deleteCommentsSuper",
        ]);
        break;
      case "App\\Models\\Record":
        // Check if the user has regular or super permission to delete Record comments
        userCanDeleteComment = CRMUtils.userHasPermission(user, [
          "record_deleteComments",
        ]);
        userCanDeleteCommentSuper = CRMUtils.userHasPermission(user, [
          "record_deleteCommentsSuper",
        ]);
        break;
      case "App\\Models\\RecordProduct":
        // Check if the user has regular or super permission to delete RecordProduct comments
        userCanDeleteComment = CRMUtils.userHasPermission(user, [
          "recordproduct_deleteComments",
        ]);
        userCanDeleteCommentSuper = CRMUtils.userHasPermission(user, [
          "recordproduct_deleteCommentsSuper",
        ]);
        break;
      case "App\\Models\\Ticket":
        // Check if the user has regular or super permission to delete Ticket comments
        userCanDeleteComment = CRMUtils.userHasPermission(user, [
          "tickets_deleteComments",
        ]);
        userCanDeleteCommentSuper = CRMUtils.userHasPermission(user, [
          "tickets_deleteCommentsSuper",
        ]);
        break;
      case "App\\Models\\Influencer":
        // Check if the user has regular or super permission to delete Influencer comments
        userCanDeleteComment = CRMUtils.userHasPermission(user, [
          "marketing_deleteInfluencerComments",
        ]);
        userCanDeleteCommentSuper = CRMUtils.userHasPermission(user, [
          "marketing_deleteInfluencerCommentsSuper",
        ]);
        break;
    }

    // Return true if the user has super permission or the user can delete and is the author of the comment
    if (
      userCanDeleteCommentSuper ||
      (userCanDeleteComment && comment.user_id == user.id)
    ) {
      return true;
    } else {
      return false;
    }
  },
};

export default CommentsUtils;
